import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Car, 
  Search, 
  Edit2, 
  Trash2, 
  Save,
  X,
  DollarSign,
  Calendar,
  Filter,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Loader2,
  Building2,
  FileText,
  Download
} from "lucide-react";
import { useBusiness } from "@/contexts/BusinessContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Modal de edición
function EditModal({ vehicle, onClose, onSave }) {
  const [formData, setFormData] = useState({
    clv: vehicle.clv || "",
    category: vehicle.category || "",
    brand: vehicle.brand || "",
    model_name: vehicle.model_name || "",
    version: vehicle.version || "",
    year: vehicle.year || "",
    price: vehicle.price || "",
    purchase_price: vehicle.purchase_price || "",
    spec_sheet_url: vehicle.spec_sheet_url || ""
  });
  const [saving, setSaving] = useState(false);
  const [uploadingSpec, setUploadingSpec] = useState(false);
  // null = "checking", true = existe, false = rota (URL apunta a archivo perdido)
  const [specSheetHealthy, setSpecSheetHealthy] = useState(null);
  const { currentBusiness } = useBusiness();
  const isAutomotriz = currentBusiness?.id === "automotriz";

  // Verificar si la ficha técnica actual sigue disponible al abrir el modal.
  useEffect(() => {
    const url = formData.spec_sheet_url;
    if (!url) { setSpecSheetHealthy(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.head(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
          validateStatus: () => true,
        });
        if (!cancelled) setSpecSheetHealthy(r.status === 200);
      } catch {
        if (!cancelled) setSpecSheetHealthy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [formData.spec_sheet_url]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSpecSheetUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) {
      toast.error("El PDF no debe pesar más de 15 MB");
      event.target.value = "";
      return;
    }
    setUploadingSpec(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (!r.data?.filename) {
        throw new Error("Respuesta sin nombre de archivo");
      }
      const newUrl = `/api/spec-sheets/${r.data.filename}`;
      // Verificar que el backend REALMENTE lo guardó antes de continuar.
      const check = await axios.head(`${process.env.REACT_APP_BACKEND_URL}${newUrl}`, {
        validateStatus: () => true,
      });
      if (check.status !== 200) {
        throw new Error("El archivo no quedó disponible tras subir");
      }
      setFormData(prev => ({ ...prev, spec_sheet_url: newUrl }));
      setSpecSheetHealthy(true);
      toast.success("Ficha técnica subida — recuerda guardar para aplicarla");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Error al subir la ficha técnica");
    } finally {
      setUploadingSpec(false);
      event.target.value = "";
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vehicles/${vehicle.id}`, formData);
      toast.success("Vehículo actualizado correctamente");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Error al actualizar el vehículo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" data-testid="edit-modal">
      <div className="w-full max-w-lg bg-[#0f1a2b] border border-slate-700 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h3 className="text-lg font-semibold text-white flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-blue-500" />
            Editar Vehículo
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <div className="p-5 space-y-4">
          {/* CLV (Clave) */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">
              CLV (Clave)
              <span className="text-xs text-blue-400 ml-2">• Aparece en cotizaciones</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.clv}
                onChange={(e) => handleChange("clv", e.target.value.toUpperCase())}
                placeholder="Ej: URV114, NP3G25..."
                className="flex-1 px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none uppercase"
                data-testid="edit-clv-input"
              />
              <button
                type="button"
                onClick={() => {
                  const model = (formData.model_name || "").trim();
                  const prefix = model
                    .replace(/[^A-Za-zÀ-ÿ0-9]/g, "")
                    .slice(0, 3)
                    .toUpperCase();
                  const combined = `${formData.version || ""} ${formData.year || ""}`;
                  const digits = (combined.match(/\d+/g) || [])[0] || "";
                  const num = digits ? digits.padStart(3, "0").slice(-3) : "";
                  const suggestion = prefix + num;
                  if (!suggestion) {
                    toast.error("Completa Modelo y Versión/Año para sugerir");
                    return;
                  }
                  handleChange("clv", suggestion);
                  toast.success(`Sugerencia: ${suggestion}`);
                }}
                className="px-4 py-3 bg-blue-500/20 hover:bg-blue-500/30 border border-blue-500/40 text-blue-300 rounded-lg text-sm font-medium transition-colors whitespace-nowrap"
                title="Generar CLV sugerido (3 letras del modelo + número de versión/año)"
                data-testid="suggest-clv-btn"
              >
                Sugerir
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Formato sugerido: 3 letras del modelo + número (ej: URV + 114 = URV114)
            </p>
          </div>

          {/* Categoría (solo automotriz) — aparece en filtros del catálogo público */}
          {isAutomotriz && (
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Categoría
                <span className="text-xs text-blue-400 ml-2">• Filtro del catálogo público</span>
              </label>
              <select
                value={formData.category}
                onChange={(e) => handleChange("category", e.target.value)}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:border-blue-500 focus:outline-none"
                data-testid="edit-category-select"
              >
                <option value="">Auto-detectar (por nombre del modelo)</option>
                <option value="Autos">Autos (sedán / hatchback / deportivo)</option>
                <option value="Camionetas">Camionetas (Pickup)</option>
                <option value="SUV">SUV</option>
                <option value="Van pasajeros">Van pasajeros</option>
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Si dejas &quot;Auto-detectar&quot;, el sistema decide por el modelo (ej. Tracker → SUV).
              </p>
            </div>
          )}

          {/* Ficha técnica (PDF) — subir o reemplazar */}
          {isAutomotriz && (
            <div>
              <label className="block text-sm text-slate-400 mb-1 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" />
                Ficha técnica (PDF)
              </label>
              <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                <div className="flex-1 px-3 py-2 bg-slate-800/60 border border-slate-700 rounded-lg text-sm">
                  {!formData.spec_sheet_url ? (
                    <div className="flex items-center gap-2 text-amber-400">
                      <span className="w-2 h-2 rounded-full bg-amber-400" />
                      <span>Sin ficha técnica</span>
                    </div>
                  ) : specSheetHealthy === false ? (
                    <div className="flex items-center gap-2 text-red-400">
                      <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
                      <span className="truncate">Ficha rota — súbela de nuevo</span>
                    </div>
                  ) : specSheetHealthy === true ? (
                    <div className="flex items-center gap-2 text-emerald-400">
                      <span className="w-2 h-2 rounded-full bg-emerald-400" />
                      <span className="truncate">Ficha cargada</span>
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${formData.spec_sheet_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ml-auto text-blue-400 hover:text-blue-300 text-xs underline"
                        data-testid="edit-spec-preview"
                      >
                        Ver PDF
                      </a>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      <span>Verificando…</span>
                    </div>
                  )}
                </div>
                <label
                  className={`shrink-0 px-4 py-2 rounded-lg cursor-pointer text-sm font-medium transition-colors ${
                    uploadingSpec
                      ? "bg-slate-700 text-slate-400 cursor-wait"
                      : specSheetHealthy === false
                        ? "bg-red-600 hover:bg-red-500 text-white"
                        : formData.spec_sheet_url
                          ? "bg-blue-600 hover:bg-blue-500 text-white"
                          : "bg-emerald-600 hover:bg-emerald-500 text-white"
                  }`}
                  data-testid="edit-spec-upload-btn"
                >
                  {uploadingSpec ? (
                    <span className="inline-flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin" /> Subiendo…
                    </span>
                  ) : specSheetHealthy === false ? (
                    "Recargar PDF"
                  ) : formData.spec_sheet_url ? (
                    "Reemplazar PDF"
                  ) : (
                    "Subir PDF"
                  )}
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    onChange={handleSpecSheetUpload}
                    disabled={uploadingSpec}
                  />
                </label>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                El PDF se guarda en Mongo y sobrevive redeploys. No pierdes fichas.
              </p>
            </div>
          )}

          {/* Marca */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Marca</label>
            <input
              type="text"
              value={formData.brand}
              onChange={(e) => handleChange("brand", e.target.value)}
              placeholder="Ej: Nissan, Toyota, Ford..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              data-testid="edit-brand-input"
            />
          </div>

          {/* Modelo */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Modelo</label>
            <input
              type="text"
              value={formData.model_name}
              onChange={(e) => handleChange("model_name", e.target.value)}
              placeholder="Ej: NP300, Hilux, Ranger..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              data-testid="edit-model-input"
            />
          </div>

          {/* Versión */}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Versión</label>
            <input
              type="text"
              value={formData.version}
              onChange={(e) => handleChange("version", e.target.value)}
              placeholder="Ej: Doble Cabina Gasolina, SR Diesel..."
              className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
              data-testid="edit-version-input"
            />
          </div>

          {/* Año y Precios en una fila */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm text-slate-400 mb-1">Año</label>
              <input
                type="text"
                value={formData.year}
                onChange={(e) => handleChange("year", e.target.value)}
                placeholder="Ej: 2025"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                data-testid="edit-year-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">Precio Venta</label>
              <input
                type="text"
                value={formData.price}
                onChange={(e) => handleChange("price", e.target.value)}
                placeholder="Ej: 529000"
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none"
                data-testid="edit-price-input"
              />
            </div>
            <div>
              <label className="block text-sm text-slate-400 mb-1">
                Precio Compra
                <span className="text-xs text-amber-500 ml-1">• Interno</span>
              </label>
              <input
                type="text"
                value={formData.purchase_price}
                onChange={(e) => handleChange("purchase_price", e.target.value)}
                placeholder="Ej: 450000"
                className="w-full px-4 py-3 bg-slate-800 border border-amber-900/50 rounded-lg text-white placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                data-testid="edit-purchase-price-input"
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-5 border-t border-slate-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-slate-400 hover:text-white transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-2 bg-blue-500 text-white rounded-lg font-medium hover:bg-blue-600 transition-colors flex items-center gap-2 disabled:opacity-50"
            data-testid="edit-save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Guardar cambios
          </button>
        </div>
      </div>
    </div>
  );
}

function DatabaseTab() {
  const { currentBusiness, businessId } = useBusiness();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [editingVehicle, setEditingVehicle] = useState(null);
  
  // Filters
  const [filterBrand, setFilterBrand] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchVehicles();
  }, [businessId]);

  const fetchVehicles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/vehicles?business_id=${businessId}`);
      if (response.data.success) {
        setVehicles(response.data.vehicles);
      }
    } catch (error) {
      toast.error("Error al cargar vehículos");
    } finally {
      setLoading(false);
    }
  };

  const deleteVehicle = async (vehicleId) => {
    if (!window.confirm("¿Eliminar este vehículo?")) return;
    try {
      await axios.delete(`${API}/vehicles/${vehicleId}`);
      toast.success("Vehículo eliminado");
      fetchVehicles();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const filteredVehicles = vehicles.filter(v => {
    const matchesSearch = 
      v.model_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.version?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      v.brand?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBrand = !filterBrand || v.brand?.toLowerCase().includes(filterBrand.toLowerCase());
    return matchesSearch && matchesBrand;
  });

  const uniqueBrands = [...new Set(vehicles.map(v => v.brand).filter(Boolean))];

  return (
    <div className="space-y-6">
      {/* Modal de edición */}
      {editingVehicle && (
        <EditModal 
          vehicle={editingVehicle}
          onClose={() => setEditingVehicle(null)}
          onSave={fetchVehicles}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-montserrat font-bold text-white flex items-center gap-3">
          {businessId === "maquinaria" ? (
            <Building2 className="w-7 h-7 text-orange-500" />
          ) : (
            <Car className="w-7 h-7 text-blue-500" />
          )}
          Base de Datos - {currentBusiness?.name || "Vehículos"}
          <span className="text-sm font-normal text-slate-400 ml-2">
            ({vehicles.length} {businessId === "maquinaria" ? "equipos" : "vehículos"})
          </span>
        </h2>
        <button
          onClick={fetchVehicles}
          className="p-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
          data-testid="refresh-btn"
        >
          <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Search and Filters */}
      <div className="glass-card rounded-lg p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
            <input
              type="text"
              placeholder="Buscar por marca, modelo o versión..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-slate-800/50 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
              data-testid="search-input"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-3 rounded-lg flex items-center gap-2 transition-colors ${
              showFilters ? 'bg-blue-500 text-white' : 'bg-slate-800 text-slate-400 hover:text-white'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtros
          </button>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-slate-700 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-slate-400 mb-1 block">Marca</label>
              <select
                value={filterBrand}
                onChange={(e) => setFilterBrand(e.target.value)}
                className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-blue-500"
              >
                <option value="">Todas las marcas</option>
                {uniqueBrands.map(brand => (
                  <option key={brand} value={brand}>{brand}</option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Vehicles Table */}
      <div className="glass-card rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-500 mx-auto mb-4" />
            <p className="text-slate-400">Cargando vehículos...</p>
          </div>
        ) : filteredVehicles.length === 0 ? (
          <div className="p-12 text-center">
            <Car className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No hay vehículos registrados</p>
            <p className="text-slate-500 text-sm mt-2">
              Los vehículos se agregan automáticamente al subir fichas técnicas en la pestaña Diseño
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-800/50">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">CLV</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Marca</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Modelo</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Versión</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">Año</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">P. Venta</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-slate-400">
                    <span className="text-amber-400">P. Compra</span>
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Ficha</th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-slate-400">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {filteredVehicles.map((vehicle) => (
                  <React.Fragment key={vehicle.id}>
                    <tr className="hover:bg-slate-800/30 transition-colors">
                      <td className="px-4 py-4">
                        <span className={vehicle.clv ? "text-blue-300 font-mono text-sm" : "text-slate-500 text-sm"}>
                          {vehicle.clv || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-white">{vehicle.brand || "-"}</td>
                      <td className="px-4 py-4 text-white font-medium">{vehicle.model_name}</td>
                      <td className="px-4 py-4">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-sm">
                          {vehicle.version}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-slate-400">{vehicle.year || "-"}</td>
                      <td className="px-4 py-4">
                        <span className={vehicle.price ? "text-green-400 font-medium" : "text-slate-500"}>
                          {vehicle.price ? `$${parseInt(vehicle.price).toLocaleString('es-MX')}` : "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        <span className={vehicle.purchase_price ? "text-amber-400 font-medium" : "text-slate-500"}>
                          {vehicle.purchase_price ? `$${parseInt(vehicle.purchase_price).toLocaleString('es-MX')}` : "-"}
                        </span>
                      </td>
                      <td className="px-4 py-4 text-center">
                        {vehicle.spec_sheet_url ? (
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${vehicle.spec_sheet_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 bg-emerald-500/20 text-emerald-400 rounded text-xs hover:bg-emerald-500/30 transition-colors"
                            title="Descargar ficha técnica"
                          >
                            <Download className="w-3 h-3" />
                            PDF
                          </a>
                        ) : (
                          <span className="text-slate-500 text-xs">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-2">
                          <button
                            onClick={() => setExpandedId(expandedId === vehicle.id ? null : vehicle.id)}
                            className="p-2 text-slate-400 hover:text-white hover:bg-slate-700 rounded transition-colors"
                            title="Ver especificaciones"
                          >
                            {expandedId === vehicle.id ? (
                              <ChevronUp className="w-4 h-4" />
                            ) : (
                              <ChevronDown className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => setEditingVehicle(vehicle)}
                            className="p-2 text-slate-400 hover:text-blue-400 hover:bg-slate-700 rounded transition-colors"
                            title="Editar vehículo"
                            data-testid={`edit-btn-${vehicle.id}`}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteVehicle(vehicle.id)}
                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-slate-700 rounded transition-colors"
                            title="Eliminar"
                            data-testid={`delete-btn-${vehicle.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === vehicle.id && (
                      <tr className="bg-slate-800/20">
                        <td colSpan="9" className="px-4 py-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <h4 className="text-sm font-medium text-slate-400 mb-2">Especificaciones Técnicas</h4>
                              <ul className="space-y-1">
                                {vehicle.specifications?.map((spec, idx) => (
                                  <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                    <span className="text-blue-500">•</span>
                                    {spec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            {vehicle.vehicle_image && (
                              <div>
                                <h4 className="text-sm font-medium text-slate-400 mb-2">Imagen</h4>
                                <img 
                                  src={vehicle.vehicle_image} 
                                  alt={vehicle.model_name}
                                  className="max-h-32 rounded"
                                />
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatabaseTab;
